import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  ARKO_CHAT_WIDTH_DEFAULT_PCT,
  ARKO_CHAT_WIDTH_LEGACY_KEY,
  arkoChatWidthStorageKey,
  clampArkoChatWidthPct,
  parseArkoChatWidthPct,
} from './portalArkoChatWidth';

export interface PortalArkoChatPaneProps {
  customerId: string;
  children: React.ReactNode;
}

/**
 * Ziehbare ARKO-Chat-Spalte (gleicher Drag wie Dokument/ARKO-Split).
 * Default = volle nutzbare Breite; Drag nur optional enger.
 * Innere Blasen-Spalte (Feed+Composer) füllt die Pane — kein 48rem-Clip.
 */
export const PortalArkoChatPane: React.FC<PortalArkoChatPaneProps> = ({
  customerId,
  children,
}) => {
  const storageKey = arkoChatWidthStorageKey(customerId);
  const containerRef = useRef<HTMLDivElement>(null);
  const [widthPct, setWidthPct] = useState(ARKO_CHAT_WIDTH_DEFAULT_PCT);
  const [hydrated, setHydrated] = useState(false);

  const draggingRef = useRef(false);
  const dragStartRef = useRef<{ startX: number; startPct: number } | null>(null);
  const dragCleanupRef = useRef<((ev?: MouseEvent) => void) | null>(null);
  const dragRafRef = useRef<number | null>(null);
  const guideRef = useRef<HTMLDivElement>(null);
  const shieldRef = useRef<HTMLDivElement>(null);
  const lastClientXRef = useRef<number | null>(null);

  const clampPct = useCallback((pct: number) => {
    const el = containerRef.current;
    const w = el?.getBoundingClientRect().width ?? 0;
    return clampArkoChatWidthPct(pct, w);
  }, []);

  const applyPct = useCallback((pct: number) => {
    containerRef.current?.style.setProperty('--arko-chat-width-pct', `${pct}%`);
  }, []);

  useLayoutEffect(() => {
    try {
      let raw = localStorage.getItem(storageKey);
      if (raw == null) raw = localStorage.getItem(ARKO_CHAT_WIDTH_LEGACY_KEY);
      const parsed = parseArkoChatWidthPct(raw);
      if (parsed != null) setWidthPct(parsed);
    } catch {
      /* ignore */
    }
    setHydrated(true);
  }, [storageKey]);

  useLayoutEffect(() => {
    if (!hydrated) return;
    setWidthPct((p) => clampPct(p));
  }, [hydrated, clampPct]);

  useEffect(() => {
    if (!hydrated || typeof ResizeObserver === 'undefined') return;
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      if (draggingRef.current) return;
      setWidthPct((p) => clampPct(p));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [hydrated, clampPct]);

  const commitPct = useCallback(
    (pct: number) => {
      const clamped = clampPct(pct);
      applyPct(clamped);
      setWidthPct(clamped);
      try {
        localStorage.setItem(storageKey, String(clamped));
      } catch {
        /* ignore */
      }
    },
    [clampPct, applyPct, storageKey],
  );

  const positionGuide = useCallback((clientX: number) => {
    const cont = containerRef.current;
    const guide = guideRef.current;
    if (!cont || !guide) return;
    const rect = cont.getBoundingClientRect();
    if (rect.width <= 0) return;
    const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
    guide.style.left = `${x}px`;
  }, []);

  const endDragVisuals = useCallback(() => {
    draggingRef.current = false;
    containerRef.current?.classList.remove('portal-split-drag-active');
    const guide = guideRef.current;
    const shield = shieldRef.current;
    if (guide) guide.style.display = 'none';
    if (shield) shield.style.display = 'none';
  }, []);

  useEffect(
    () => () => {
      if (dragRafRef.current != null) {
        cancelAnimationFrame(dragRafRef.current);
        dragRafRef.current = null;
      }
      dragCleanupRef.current?.();
      dragCleanupRef.current = null;
      endDragVisuals();
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    },
    [endDragVisuals],
  );

  const onDividerMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      e.preventDefault();
      dragCleanupRef.current?.();

      const cont = containerRef.current;
      if (!cont) return;

      dragStartRef.current = { startX: e.clientX, startPct: widthPct };
      lastClientXRef.current = e.clientX;
      draggingRef.current = true;
      cont.classList.add('portal-split-drag-active');

      const guide = guideRef.current;
      const shield = shieldRef.current;
      if (guide) {
        guide.style.display = 'block';
        positionGuide(e.clientX);
      }
      if (shield) shield.style.display = 'block';

      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';

      const onMove = (ev: MouseEvent) => {
        if (!dragStartRef.current) return;
        lastClientXRef.current = ev.clientX;
        if (dragRafRef.current != null) return;
        dragRafRef.current = requestAnimationFrame(() => {
          dragRafRef.current = null;
          const x = lastClientXRef.current;
          if (x == null) return;
          positionGuide(x);
        });
      };

      const onUp = (ev?: MouseEvent) => {
        if (dragRafRef.current != null) {
          cancelAnimationFrame(dragRafRef.current);
          dragRafRef.current = null;
        }

        const st = dragStartRef.current;
        const container = containerRef.current;
        let finalPct = widthPct;
        if (st && container) {
          const w = container.getBoundingClientRect().width;
          if (w > 0) {
            const clientX = ev?.clientX ?? lastClientXRef.current ?? st.startX;
            const deltaPct = ((clientX - st.startX) / w) * 100;
            finalPct = clampPct(st.startPct + deltaPct);
          }
        }

        lastClientXRef.current = null;
        dragStartRef.current = null;
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        dragCleanupRef.current = null;
        endDragVisuals();
        commitPct(finalPct);
      };

      dragCleanupRef.current = onUp;
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [widthPct, clampPct, positionGuide, endDragVisuals, commitPct],
  );

  return (
    <div
      ref={containerRef}
      style={{ '--arko-chat-width-pct': `${widthPct}%` } as React.CSSProperties}
      className="portal-arko-chat-split relative flex flex-1 min-h-0 min-w-0 w-full items-stretch bg-transparent"
    >
      <div
        ref={shieldRef}
        className="portal-split-drag-shield absolute inset-0 z-[15] hidden cursor-col-resize lg:block"
        style={{ display: 'none' }}
        aria-hidden
      />
      <div
        ref={guideRef}
        className="portal-split-drag-guide pointer-events-none absolute top-0 bottom-0 z-20 hidden w-0.5 -translate-x-1/2 bg-violet-500/75 shadow-[0_0_10px_rgba(139,92,246,0.45)] lg:block"
        style={{ display: 'none', left: 0 }}
        aria-hidden
      />

      <section
        className="portal-arko-pane relative flex flex-1 min-h-0 min-w-0 flex-col h-full w-full lg:flex-none lg:w-[min(100%,var(--arko-chat-width-pct))] lg:max-w-full lg:flex-shrink-0"
        aria-label="ARKO Chat"
      >
        {children}
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="ARKO-Chat-Breite anpassen"
          onMouseDown={onDividerMouseDown}
          className="hidden lg:block absolute top-0 right-0 bottom-0 z-10 w-1 cursor-col-resize bg-white/[0.04] hover:bg-violet-600/40 transition-colors after:absolute after:inset-y-0 after:-left-1 after:-right-1 after:content-['']"
        />
      </section>
    </div>
  );
};
